import bind from 'bind-decorator';
import * as React from 'react';
import { MessageDescriptor } from 'react-intl';

import iconFacebook from 'assets/images/icons/facebook.svg';
import iconInstagram from 'assets/images/icons/instagram.svg';
import iconLinkedin from 'assets/images/icons/linkedin.svg';
import iconTwitter from 'assets/images/icons/twitter.svg';
import iconYoutube from 'assets/images/icons/youtube.png';
import iconPinterest from 'assets/images/icons/pinterest.svg';

import { ROUTES } from 'global/constants';
import { NavLink } from 'react-router-dom';
import { UI_IS_SM } from 'ui/breakpoints';
import { UnderConstruction } from 'ui/components';
import { devDemoPageEnabled } from 'ui/DevDemoPage';
import { Collapsible } from 'ui/components/Collapsible';
import { WrappedMessage } from 'utils';
import { HarvardUniversityLogo } from '../HarvardUniversityLogo';
import { MailSubscribeBox } from '../MailSubscribeBox';
import messages from './displayMessages';
import { intl } from 'i18n';

interface FooterGroupProps {
    title: string;
    children: React.ReactNode;
}

interface FooterGroupState {
    isCollapsible: boolean;
    isOpen: boolean;
}

export class FooterGroup extends React.Component<FooterGroupProps, FooterGroupState> {

    public constructor(props: FooterGroupProps) {
        super(props);
        this.state = {
            isCollapsible: this.isMobileBreakpoint(),
            isOpen: false,
        };
    }

    public componentDidMount() {
        window.addEventListener('resize', this.onResize);
        this.onResize();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
    }

    public render() {
        let indicator = null;
        if (this.state.isCollapsible) {
            indicator = (
                <span className='footer-group-title-indicator'>{this.state.isOpen ? '▲' : '▼'}</span>
            );
        }
        const title = (
            <div className='footer-caption'>
                {this.props.title} {indicator}
            </div>
        );
        return (
            <Collapsible
                title={title}
                isOpen={this.state.isOpen}
                isCollapsible={this.state.isCollapsible}
                onToggle={this.onToggle} >

                {this.props.children}

            </Collapsible>
        );
    }

    @bind private onToggle() {
        this.setState({
            isOpen: !this.state.isOpen,
        });
    }

    @bind private isMobileBreakpoint() {
        return UI_IS_SM.matches;
    }

    @bind private onResize() {
        this.setState({
            isCollapsible: this.isMobileBreakpoint(),
        });
    }
}

const footerLinks = {

    main: [
        {
            message: messages.footerDashboard,
            url: ROUTES.Dashboard.HOME,
            clickable: true,
            hidden: false,
        },
        {
            message: messages.footerLibrary,
            url: ROUTES.Library.HOME,
            clickable: true,
            hidden: false,
        },
        {
            message: messages.footerPeople,
            url: ROUTES.People.HOME,
            clickable: true,
            hidden: false,
        },
        {
            message: messages.footerClasses,
            url: ROUTES.Dashboard.LEARNER_CLASSROOMS,
            clickable: true,
            hidden: false,
        },
        {
            message: messages.footerDiscussion,
            url: ROUTES.Community.DISCUSSIONS,
            clickable: true,
            hidden: false,
        },
        {
            message: messages.devDemoPage,
            url: ROUTES.Test.UI_DEMOS,
            // dev demo page link only shown if enabled
            clickable: devDemoPageEnabled,
            hidden: !devDemoPageEnabled,
        },
    ],

    resources: [
        {
            message: messages.footerAbout,
            url: ROUTES.General.ABOUT_SITE,
            clickable: true,
        },
        {
            message: messages.footerBlog,
            url: ROUTES.General.BLOG,
            clickable: true,
        },
        {
            message: messages.footerCollaborators,
            url: ROUTES.Organizations.COLLABORATORS,
            clickable: true,
        },
        {
            message: messages.footerTeam,
            url: ROUTES.General.TEAM,
            clickable: true,
        },
        {
            message: messages.footerTermsOfService,
            url: ROUTES.General.TOS,
            clickable: true,
        },
        {
            message: messages.footerPrivacyPolicy,
            url: ROUTES.General.PRIVACY_POLICY,
            clickable: true,
        },
        {
            message: messages.footerAccessibility,
            url: ROUTES.General.ACCESSIBILITY,
            clickable: true,
            newTab: true,
        },
        {
            message: messages.footerOpenEdxLink,
            url: ROUTES.General.OPEN_EDX,
            clickable: true,
            newTab: true,
        },
    ],

    support: [
        {
            message: messages.footerHelpCenter,
            url: ROUTES.General.HELP_CENTER,
            clickable: true,
            newTab: true,
        },
        {
            message: messages.footerTutorials,
            url: ROUTES.General.TUTORIALS,
            clickable: true,
            newTab: true,
        },
        {
            message: messages.footerGettingStarted,
            url: ROUTES.General.GETTING_STARTED,
            clickable: true,
            newTab: true,
        },
        {
            message: messages.footerTeacherResources,
            url: ROUTES.General.TEACHER_RESOURCES,
            clickable: true,
            newTab: true,
        },
    ],

    social: [
        {
            message: 'Facebook',
            url: 'https://www.facebook.com/LabXchange',
            img: iconFacebook,
            hidden: false,
            newTab: true,
        },
        {
            message: 'Twitter',
            url: 'https://twitter.com/LabXchange',
            img: iconTwitter,
            hidden: false,
            newTab: true,
        },
        {
            message: 'Instagram',
            url: 'https://www.instagram.com/labxchange/',
            img: iconInstagram,
            hidden: false,
            newTab: true,
        },
        {
            message: 'LinkedIn',
            url: 'https://www.linkedin.com/company/labxchange',
            img: iconLinkedin,
            hidden: false,
            newTab: true,
        },
        {
            message: 'Pinterest',
            url: 'https://www.pinterest.com/labxchange/',
            img: iconPinterest,
            hidden: false,
            newTab: true,
        },
        {
            message: 'YouTube',
            url: 'https://www.youtube.com/c/labxchange',
            img: iconYoutube,
            hidden: false,
            newTab: true,
        },
    ],
};

interface FooterLinkProps {
    message: MessageDescriptor;
    url: string;
    clickable: boolean;
    newTab?: boolean;
}

export const FooterLink: React.FunctionComponent<FooterLinkProps> = (props) => {
    if (props.clickable) {
        if (props.url.startsWith('http')) {
            return (
                <a target={props.newTab ? '_blank' : undefined}
                    tabIndex={0} className='footer-link' href={props.url}>
                    <WrappedMessage message={props.message}></WrappedMessage>
                </a>
            );
        } else {
            return (
                <NavLink tabIndex={0} className='footer-link' to={props.url}>
                    <WrappedMessage message={props.message}></WrappedMessage>
                </NavLink>
            );
        }
    } else {
        return (
            <UnderConstruction>
                <NavLink tabIndex={0} className='footer-link' to={props.url}>
                    <WrappedMessage message={props.message}></WrappedMessage>
                </NavLink>
            </UnderConstruction>
        );
    }
};

interface FooterProps {
    backgroundUrl?: string;
    footerFeature?: React.ReactNode;
}

export const Footer: React.FunctionComponent<FooterProps> =
    ({
        backgroundUrl,
        footerFeature,
    }) => (
        <>
            <div className='footer-feature'>
                {footerFeature ? footerFeature : null}
            </div>
            <footer>
                <div className='footer-harvard-panel'>
                    <HarvardUniversityLogo width={214} height={54} />
                </div>
                <div className='footer-main' style={
                    backgroundUrl
                        ? { backgroundImage: `url(${backgroundUrl})` }
                        : {}
                }>

                    <div className='footer-row'>
                        <div className='footer-group'>
                            <FooterGroup title={intl.formatMessage(messages.footerCaptionBrand)} >
                                {footerLinks.main.filter((link) => !link.hidden).map((link, i) => {
                                    return (
                                        <FooterLink key={i} {...link} />
                                    );
                                })}
                            </FooterGroup>
                        </div>
                        <div className='footer-group'>
                            <FooterGroup title={intl.formatMessage(messages.footerResources)} >
                                {footerLinks.resources.map((link, i) => {
                                    return (
                                        <FooterLink key={i} {...link} />
                                    );
                                })}
                            </FooterGroup>
                        </div>
                    </div>
                    <div className='footer-row'>
                        <div className='footer-group'>
                            <FooterGroup title={intl.formatMessage(messages.footerSupport)} >
                                {footerLinks.support.map((link, i) => {
                                    return (
                                        <FooterLink key={i} {...link} />
                                    );
                                })}
                            </FooterGroup>
                        </div>
                        <div className='footer-group'>
                            <FooterGroup title={intl.formatMessage(messages.footerStayConnected)} >
                                <FooterLink
                                    newTab={true}
                                    message={messages.footerContactUs}
                                    url={ROUTES.General.CONTACT_US}
                                    clickable={true}
                                />
                                <FooterLink
                                    newTab={true}
                                    message={messages.footerShareStory}
                                    url={ROUTES.General.SHARE_STORY}
                                    clickable={true}
                                />
                                <FooterLink
                                    newTab={true}
                                    message={messages.footerNews}
                                    url={ROUTES.General.NEWS}
                                    clickable={true}
                                />
                            </FooterGroup>
                            <WrappedMessage message={messages.footerStayConnectedInfo} />

                            <MailSubscribeBox className='footer-subscribe-box' />

                            {footerLinks.social.filter((link) => !link.hidden).map((link, i) => {
                                return (
                                    <a key={i} tabIndex={0} target={link.newTab ? '_blank' : undefined} href={link.url}>
                                        <img alt={link.message} src={link.img} />
                                    </a>
                                );
                            })}

                        </div>
                    </div>

                </div>
                <div className='footer-notes'>
                    <WrappedMessage message={messages.footerCopyright} values={{ year: new Date().getFullYear() }} />
                </div>
            </footer>
        </>
    );
